import { IndElement, IndPhrase } from "@shared/models/cat.model";
import { DrawerDetails } from "@shared/models/common";
import { EmbeddedObjectAnnotation, EmbeddedObjectAnnotationDto } from "@shared/models/embedded-object-annotation";
import { RevRule } from "@shared/models/rev.model";


interface ViewModelBase {
        type: string;
} 

interface ConsistencyIssueClaimViewModel extends ViewModelBase {
    type: 'claim_consistency_issue';
    data: {
        idToCheck: number
        drawerDetails: DrawerDetails
    }
}

interface ConsistencyIssueReferenceViewModel extends ViewModelBase {
    type: 'reference_consistency_issue';
    data: EmbeddedObjectAnnotation
}

interface ConsistencyIssueCatImageViewModel extends ViewModelBase {
    type: 'cat_image_consistency_issue';
    data:  {
        idToCheck: number
        drawerDetails: DrawerDetails
    } 
}


interface ConsistencyIssueClaimImageViewModel extends ViewModelBase {
    type: 'claim_image_consistency_issue';
    data:  DrawerDetails
     
}
interface ConsistencyAssocImageViewModel extends ViewModelBase {
    type: 'assoc_image_consistency_issue';
    data:  {
        assocImageId: number;
        isTable: boolean
    }
     
}

export type ConsistencyIssueViewModel = ConsistencyIssueClaimViewModel | ConsistencyIssueReferenceViewModel | ConsistencyIssueCatImageViewModel | ConsistencyIssueClaimImageViewModel | ConsistencyAssocImageViewModel;

export interface ConsistencyIssueDto {

  issueId: number;
  conclusion: string;
  reason: string;
  explanation: string;
  category: string;
  convertible: boolean;
  consistencyCheckType: string;
  refDocId: number;
  catPhraseId: number;
  refDocAnchorStartPhraseId: number;
  refDocAnchorEndPhraseId: number;
  refDocAnchorId: number;
  embeddedObjectAnnotation: EmbeddedObjectAnnotationDto;
  docPhraseId: number;
  catImageId: number;
  showCrossedEyeIcon: boolean;
  catPhraseTxt: string;
  assocImageId: number;
  isTable: boolean;

}

export abstract class ConsistencyIssue {

    issueId: number;
    category: string;
    reason: string;
    consistencyCheckType: string;
    conclusion: string;
    convertible: boolean = false;
    icon: string;
    tooltip: string;
    _isExplanationExpanded: boolean = false
    explanation: string;

    constructor(issueId: number, explanation: string, category: string, reason: string, consistencyCheckType: string, conclusion: string, convertible: boolean, icon: string, tooltip: string) {
        this.issueId = issueId;
        this.explanation = explanation;
        this.category = category;
        this.reason = reason;
        this.consistencyCheckType = consistencyCheckType;
        this.conclusion = conclusion;
        this.convertible = convertible;
        this.icon = icon;
        this.tooltip = tooltip;
    }
    

    abstract view(): ConsistencyIssueViewModel

    remove(element: IndElement | RevRule): void {

        element.consistencyIssuesToUpdate.is_ref_consistent_anchor_update_only = false;

        this.doRemove(element);
        
    }

    // Optional hook: Default to empty so derived classes only 
    // implement it if they actually have specific undo logic!
    protected doRestore(): void {
        // Optional base cleanup

        // reset is_ref_consistent_anchor_update_only to true
        // if no consistency issues to be removed
    }    

    abstract doRemove(element: IndElement | RevRule): void;

    markAsConsistent(element: IndElement | RevRule): void {
        element.consistencyIssuesToUpdate.is_ref_consistent_anchor_update_only = false;

        
    }


}

export class ConsistencyIssueClaim extends ConsistencyIssue {

    catPhraseId: number;
    catPhraseTxt: string;
    catVersionId: number;

    constructor(issueId: number, explanation: string, category: string, reason: string, consistencyCheckType: string, conclusion: string, convertible: boolean, icon: string, tooltip: string, catPhraseId:number, catPhraseTxt: string, catVersionId: number) {
        super(issueId, explanation, category, reason, consistencyCheckType, conclusion, convertible, icon, tooltip);

        this.catPhraseId = catPhraseId;
        this.catPhraseTxt = catPhraseTxt;
        this.catVersionId = catVersionId;
    }

    view(): ConsistencyIssueViewModel {

        let drawerDetails = new DrawerDetails();
        drawerDetails.isImage = false;
        drawerDetails.phraseTxt = this.catPhraseTxt;

        return  {
                type: 'claim_consistency_issue',
                data: {
                    idToCheck: this.catPhraseId,
                    drawerDetails: drawerDetails
                    }
            };
    }
    doRemove(element: IndElement | RevRule): void {
      let claimConsistencyCheckResult = element.consistencyIssuesToUpdate?.remove.phrase_claim_consistency_check_results;

      claimConsistencyCheckResult.push({
        issue_id: this.issueId,
        cat_version_id: this.catVersionId,
        cat_phrase_id: this.catPhraseId
      }); 
    }
}

export class ConsistencyIssueReference extends ConsistencyIssue {

    embeddedObjectAnnotation: EmbeddedObjectAnnotation;
    refDocAnchorId: number;
    refDocAnchorPhraseId: number;
    catPhraseTxt: string;
    refDocId: number;
    consistencyIssueAssocImgParent: ConsistencyIssueAssocImage

    constructor(issueId: number, explanation: string, category: string, reason: string, consistencyCheckType: string, conclusion: string, convertible: boolean,
        icon: string, tooltip: string, refDocAnchorId: number, refDocAnchorPhraseId: number, catPhraseTxt: string, refDocId: number,
        embeddedObjectAnnotation: EmbeddedObjectAnnotation, consistencyIssueAssocImgParent: ConsistencyIssueAssocImage
    ) {
            super(issueId, explanation, category, reason, consistencyCheckType, conclusion, convertible, icon, tooltip);
            this.refDocAnchorId = refDocAnchorId;
            this.refDocAnchorPhraseId = refDocAnchorPhraseId;
            this.catPhraseTxt = catPhraseTxt;
            this.refDocId = refDocId;
            this.embeddedObjectAnnotation = embeddedObjectAnnotation;
            this.consistencyIssueAssocImgParent = consistencyIssueAssocImgParent;
    }

    view(): ConsistencyIssueViewModel {
        return {
            type: 'reference_consistency_issue',
            data: this.embeddedObjectAnnotation
        }
    }
    doRemove(element: IndElement | RevRule): void {
        let consistencyIssuesToUpdate = element.consistencyIssuesToUpdate;
        let anchorsToRemove = consistencyIssuesToUpdate.remove.phrase_reference_consistency_check_results;
        anchorsToRemove.push({
        issue_id: this.issueId,
        ref_doc_id: this.refDocId,
        ref_doc_anchor_id: this.refDocAnchorId
        });
    }
    markAsConsistent(element: IndElement | RevRule): void {
        if (this.convertible) {
            // mark as consistent
            let consistencyIssuesToUpdate = element.consistencyIssuesToUpdate;
            consistencyIssuesToUpdate.convert.anchors.push({
                issue_id: this.issueId,
                ref_doc_id: this.refDocId,
                ref_doc_anchor_id: this.refDocAnchorId,
                conclusion: this.conclusion,
                new_conclusion: "CONSISTENT"
                });
        }
    }
}

export class ConsistencyIssueImageReference extends ConsistencyIssueReference {

    doRemove(element: IndElement | RevRule): void {
        let consistencyIssuesToUpdate = element.consistencyIssuesToUpdate;
        if (this.consistencyIssueAssocImgParent !== null) {
            consistencyIssuesToUpdate.remove.phrase_assoc_image_reference_consistency_check_results.push({
                assoc_image_id: this.consistencyIssueAssocImgParent.assocImgId,
                is_table: this.consistencyIssueAssocImgParent.isTable,
                issue_id: this.issueId
            })            
        } else {
            let anchorsToRemove = consistencyIssuesToUpdate.remove.image_reference_consistency_check_results;
            anchorsToRemove.push({
                issue_id: this.issueId
            });
    }
    }
}

export class ConsistencyIssueCatImage extends ConsistencyIssue {

    catImageId: number;
    catVersionId: number;
    embeddedObjectAnnotation: EmbeddedObjectAnnotation;
    consistencyIssueAssocImgParent: ConsistencyIssueAssocImage

    constructor(issueId: number, explanation: string, category: string, reason: string, consistencyCheckType: string, conclusion: string, convertible: boolean, icon: string, tooltip: string,
        catImageId:number, catVersionId: number, embeddedObjectAnnotation: EmbeddedObjectAnnotation, consistencyIssueAssocImgParent: ConsistencyIssueAssocImage
    ) {
            super(issueId, explanation, category, reason, consistencyCheckType, conclusion, convertible, icon, tooltip);
            
            this.catImageId = catImageId;
            this.catVersionId = catVersionId;
            this.embeddedObjectAnnotation = embeddedObjectAnnotation;
            this.consistencyIssueAssocImgParent = consistencyIssueAssocImgParent;
    }
    
    view(): ConsistencyIssueViewModel {
        let drawerDetails = new DrawerDetails();
        drawerDetails.isImage = true;  
        drawerDetails.issueDescription = this.reason;
        drawerDetails.url = this.embeddedObjectAnnotation?.imgUrl;  
        return  {
                type: 'cat_image_consistency_issue',
                data: {
                    idToCheck: this.catImageId,
                    drawerDetails: drawerDetails
                }
            };
    }
    doRemove(element: IndElement | RevRule): void {
        let consistencyIssuesToUpdate = element.consistencyIssuesToUpdate;

        if (this.consistencyIssueAssocImgParent !== null) {
            consistencyIssuesToUpdate.remove.phrase_assoc_image_cat_image_consistency_check_results.push({
                assoc_image_id: this.consistencyIssueAssocImgParent.assocImgId,
                is_table: this.consistencyIssueAssocImgParent.isTable,
                issue_id: this.issueId
            })

        } else {
            consistencyIssuesToUpdate.remove.image_cat_image_consistency_check_results.push({

                issue_id: this.issueId

            });
        }

    }
}

export class ConsistencyIssueImageClaim extends ConsistencyIssue {

    embeddedObjectAnnotation: EmbeddedObjectAnnotation;
    consistencyIssueAssocImgParent: ConsistencyIssueAssocImage = null;

    constructor(issueId: number, explanation: string, category: string, reason: string, consistencyCheckType: string, conclusion: string, convertible: boolean, icon: string, tooltip: string, embeddedObjectAnnotation: EmbeddedObjectAnnotation, consistencyIssueAssocImgParent: ConsistencyIssueAssocImage) {
        
        super(issueId, explanation, category, reason, consistencyCheckType, conclusion, convertible, icon, tooltip);
        this.embeddedObjectAnnotation = embeddedObjectAnnotation;
        this.consistencyIssueAssocImgParent = consistencyIssueAssocImgParent;
    }

    view(): ConsistencyIssueViewModel {
        let drawerDetails = new DrawerDetails();
        drawerDetails.isImage = false;  
        drawerDetails.phraseTxt = this.embeddedObjectAnnotation?.phrase;    
        return  {
                type: 'claim_image_consistency_issue',
                data: drawerDetails
            };
    }
    doRemove(element: IndElement | RevRule): void {
        let consistencyIssuesToUpdate = element.consistencyIssuesToUpdate;

        if (this.consistencyIssueAssocImgParent != null) {
            
            consistencyIssuesToUpdate.remove.phrase_assoc_image_claim_consistency_check_results.push({
                assoc_image_id: this.consistencyIssueAssocImgParent.assocImgId,
                is_table: this.consistencyIssueAssocImgParent.isTable,
                issue_id: this.issueId
            })
        } else {
        
        consistencyIssuesToUpdate.remove.image_claim_consistency_check_results.push({
            issue_id: this.issueId,
 
        });
    }
    }
}

export class ConsistencyIssueAssocImage extends ConsistencyIssue {
    
    isTable: boolean;
    assocImgId: number;

    constructor(issueId: number, explanation: string, category: string, reason: string, consistencyCheckType: string, conclusion: string, convertible: boolean, icon: string, tooltip: string, assocImgId: number, isTable: boolean) {
        super(issueId, explanation, category, reason, consistencyCheckType, conclusion, convertible, icon, tooltip);
        this.assocImgId = assocImgId;
        this.isTable = isTable;
    }
    
    view(): ConsistencyIssueViewModel {
    
        return {
                type: 'assoc_image_consistency_issue',
                data:  {
                    assocImageId: this.assocImgId,
                    isTable: this.isTable
                }
        };
        
    }
    doRemove(element: IndElement | RevRule): void {}

}